import { z } from "zod";
import type { ExecutableDiscoveryModel } from "./discovery-model";
import type { DiscoveryAnswers, DiscoveryQuestion } from "./types";

export const navigationSchema = z
  .object({
    startQuestionId: z.string().min(1),
    routes: z.record(
      z.string(),
      z
        .object({
          defaultNext: z.string().min(1).nullable(),
          answers: z.record(z.string(), z.string().min(1).nullable()),
        })
        .strict(),
    ),
  })
  .strict();
export type DiscoveryNavigation = z.infer<typeof navigationSchema>;

/** Temporary legacy compatibility. Derived only; never added to a loaded model. */
export function linearNavigation(questions: readonly DiscoveryQuestion[]): DiscoveryNavigation {
  if (!questions.length) throw new Error("Discovery requires questions.");
  return {
    startQuestionId: questions[0]!.id,
    routes: Object.fromEntries(
      questions.map((q, i) => [
        q.id,
        {
          defaultNext: questions[i + 1]?.id ?? null,
          answers: {},
        },
      ]),
    ),
  };
}

/** Mirrors shared/discovery_model.py, including effective reachability and all-edge cycle checks. */
export function validateNavigation(
  value: unknown,
  questions: readonly DiscoveryQuestion[],
): DiscoveryNavigation {
  const navigation = navigationSchema.parse(value);
  const byId = new Map(questions.map((q) => [q.id, q]));
  if (!byId.has(navigation.startQuestionId)) throw new Error("Invalid navigation start question.");
  const routes = Object.entries(navigation.routes);
  if (routes.length !== byId.size || routes.some(([id]) => !byId.has(id)))
    throw new Error("Incomplete navigation coverage.");
  const effective = new Map<string, Set<string>>();
  const declared = new Map<string, Set<string>>();
  for (const [id, route] of routes) {
    const q = byId.get(id)!;
    const overrides = Object.keys(route.answers);
    if (overrides.length && q.kind !== "cards" && q.kind !== "chips")
      throw new Error("Navigation overrides require a single-choice question.");
    const options = new Set("options" in q ? q.options.map((o) => o.id) : []);
    if (overrides.some((id) => !options.has(id)))
      throw new Error("Invalid navigation option reference.");
    const targets = [route.defaultNext, ...Object.values(route.answers)];
    if (targets.some((id) => id !== null && !byId.has(id)))
      throw new Error("Invalid navigation question reference.");
    declared.set(id, new Set(targets.filter((id) => id !== null)));
    const usable = Object.values(route.answers);
    if (!(q.required !== false && options.size && overrides.length === options.size))
      usable.push(route.defaultNext);
    effective.set(id, new Set(usable.filter((id) => id !== null)));
  }
  const reachable = new Set<string>();
  const pending = [navigation.startQuestionId];
  while (pending.length) {
    const id = pending.pop()!;
    if (reachable.has(id)) continue;
    reachable.add(id);
    pending.push(...effective.get(id)!);
  }
  if (reachable.size !== byId.size) throw new Error("Unreachable navigation questions.");
  const indegree = new Map(questions.map((q) => [q.id, 0]));
  for (const targets of declared.values())
    for (const id of targets) indegree.set(id, indegree.get(id)! + 1);
  const roots = [...indegree].filter(([, n]) => n === 0).map(([id]) => id);
  let visited = 0;
  while (roots.length) {
    const id = roots.pop()!;
    visited++;
    for (const target of declared.get(id)!) {
      const degree = indegree.get(target)! - 1;
      indegree.set(target, degree);
      if (degree === 0) roots.push(target);
    }
  }
  if (visited !== byId.size) throw new Error("Navigation cycles are not supported.");
  return navigation;
}

export function resolveDiscoveryPath(model: ExecutableDiscoveryModel, input: DiscoveryAnswers) {
  const entries = model.scenario.steps
    .flatMap((step) => step.questions.map((question) => ({ question, stepId: step.id })))
    .map((entry, questionIndex) => ({ ...entry, questionIndex }));
  const byId = new Map(entries.map((entry) => [entry.question.id, entry]));
  // Presence matters: explicitly malformed navigation must never take the fallback.
  const navigation = Object.hasOwn(model, "navigation")
    ? validateNavigation(
        model.navigation,
        entries.map((e) => e.question),
      )
    : linearNavigation(entries.map((e) => e.question));
  const nextQuestionId = (id: string): string | null => {
    const entry = byId.get(id);
    const route = navigation.routes[id];
    if (!entry || !route) throw new Error(`Unknown navigation question ${id}.`);
    const answer = input[id];
    return (entry.question.kind === "cards" || entry.question.kind === "chips") &&
      typeof answer === "string" &&
      Object.hasOwn(route.answers, answer)
      ? route.answers[answer]!
      : route.defaultNext;
  };
  const path: typeof entries = [];
  const questionIds = new Set<string>();
  let id: string | null = navigation.startQuestionId;
  while (id !== null) {
    if (questionIds.has(id)) throw new Error("Navigation cycles are not supported.");
    questionIds.add(id);
    path.push(byId.get(id)!);
    id = nextQuestionId(id);
  }
  const answers = Object.fromEntries(Object.entries(input).filter(([id]) => questionIds.has(id)));
  // Traverse the same validated graph with active answers. Unresolved choices
  // retain all executable successors; shared questions count only once.
  const relevantQuestionIds = new Set<string>();
  const pending = [navigation.startQuestionId];
  while (pending.length) {
    const current = pending.pop()!;
    if (relevantQuestionIds.has(current)) continue;
    relevantQuestionIds.add(current);
    const question = byId.get(current)!.question;
    const route = navigation.routes[current]!;
    if (answers[current] !== undefined) {
      const next = nextQuestionId(current);
      if (next !== null) pending.push(next);
    } else {
      const options = "options" in question ? question.options : [];
      const targets = Object.values(route.answers);
      if (!(question.required !== false && options.length && Object.keys(route.answers).length === options.length)) targets.push(route.defaultNext);
      pending.push(...targets.filter((id): id is string => id !== null));
    }
  }
  const position = (id: string) => path.findIndex((entry) => entry.question.id === id);
  return {
    path,
    questionIds,
    answers,
    relevantQuestionIds,
    questionCount: relevantQuestionIds.size,
    removedAnswerIds: Object.keys(input).filter((id) => !questionIds.has(id)),
    startQuestionId: navigation.startQuestionId,
    nextQuestionId,
    previousQuestionId: (id: string) => path[position(id) - 1]?.question.id ?? null,
    isTerminal: (id: string) => questionIds.has(id) && nextQuestionId(id) === null,
    // Preserve the completed-question numerator and completion semantics.
    progress: (id: string, complete = false) =>
      complete ? 100 : Math.round((Math.max(0, position(id)) / relevantQuestionIds.size) * 100),
    cursor: (currentQuestionId?: string | null, legacyIndex?: number) => {
      const candidate =
        currentQuestionId ??
        (!Object.hasOwn(model, "navigation") && legacyIndex !== undefined
          ? entries[legacyIndex]?.question.id
          : navigation.startQuestionId);
      return (
        path.find((e) => e.question.id === candidate) ??
        path.find((e) => e.question.required !== false && answers[e.question.id] === undefined) ??
        path[0]!
      );
    },
  };
}
